import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { fetchTransactionHistoryReport } from "../api/fetchTransactionHistoryReport/fetchTransactionHistoryReportApi";
import {
  FetchTransactionHistoryReportParams,
  TransactionHistoryReportItem,
  TransactionHistoryReportTotals,
} from "../api/fetchTransactionHistoryReport/types";
import { AppDispatch, AppThunk } from "@/app/store";

interface TransactionHistoryReportState {
  items: TransactionHistoryReportItem[];
  totalCount: number;
  totals: TransactionHistoryReportTotals | null;
  loading: boolean;
  error: string | null;
}

const initialState: TransactionHistoryReportState = {
  items: [],
  totalCount: 0,
  totals: null,
  loading: false,
  error: null,
};

const transactionHistoryReportSlice = createSlice({
  name: "transactionHistoryReport",
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
    },
    setReport: (
      state,
      action: PayloadAction<{
        items: TransactionHistoryReportItem[];
        totalCount: number;
        totals: TransactionHistoryReportTotals | null;
      }>,
    ) => {
      state.items = action.payload.items;
      state.totalCount = action.payload.totalCount;
      state.totals = action.payload.totals;
    },
  },
});

export const { setLoading, setError, setReport } =
  transactionHistoryReportSlice.actions;

export const fetchTransactionHistoryReportAction =
  (params: FetchTransactionHistoryReportParams = {}): AppThunk =>
  async (dispatch: AppDispatch) => {
    dispatch(setLoading(true));
    try {
      const response = await fetchTransactionHistoryReport(params);
      dispatch(
        setReport({
          items: response.data,
          totalCount: response.meta?.totalCount ?? 0,
          totals: response.meta?.totals ?? null,
        }),
      );
    } catch (error) {
      dispatch(
        setError(
          error instanceof Error
            ? error.message
            : "Failed to fetch transaction history report",
        ),
      );
    } finally {
      dispatch(setLoading(false));
    }
  };

export default transactionHistoryReportSlice.reducer;
